// Share dialog — lets an account OWNER (or admin) grant/revoke READ-ONLY access
// to another registered user, per account. Thin UI over the SECURITY DEFINER
// RPCs veritas.grant_account_view / revoke_account_view / list_account_viewers,
// which enforce the owner-or-admin check server-side (this dialog is only
// rendered for owners, but the RPC is the real boundary).
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, UserPlus } from 'lucide-react';
import { veritas } from '@/lib/supabase';

type Viewer = {
  viewer_email: string;
  granted_at: string;
  revoked_at: string | null;
};

export function ShareDialog({
  open,
  onClose,
  accountLogin,
}: {
  open: boolean;
  onClose: () => void;
  accountLogin: number;
}) {
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await veritas.rpc('list_account_viewers', {
      p_account_login: accountLogin,
    });
    if (error) { setError(error.message); return; }
    setViewers((data ?? []) as Viewer[]);
  }, [accountLogin]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNotice(null);
    setEmail('');
    refresh();
  }, [open, refresh]);

  if (!open) return null;

  const active = viewers.filter((v) => !v.revoked_at);

  async function grant(e: FormEvent) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setBusy(true); setError(null); setNotice(null);
    const { error } = await veritas.rpc('grant_account_view', {
      p_account_login: accountLogin,
      p_viewer_email: addr,
    });
    setBusy(false);
    if (error) {
      // Friendly mapping for the RPC's RAISEs (no such user / self / not owner).
      setError(
        /no registered user/i.test(error.message)
          ? `No Veritas account found for "${addr}". They must sign up first.`
          : /yourself/i.test(error.message)
            ? 'You already own this account.'
            : error.message,
      );
      return;
    }
    setNotice(`Shared with ${addr} (read-only).`);
    setEmail('');
    refresh();
  }

  async function revoke(addr: string) {
    if (!confirm(`Remove ${addr}'s read-only access to this account?`)) return;
    setBusy(true); setError(null); setNotice(null);
    const { error } = await veritas.rpc('revoke_account_view', {
      p_account_login: accountLogin,
      p_viewer_email: addr,
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setNotice(`Removed ${addr}.`);
    refresh();
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
      onClick={() => !busy && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full flex-col rounded-t-xl border border-line bg-bg-1 shadow-2xl sm:max-w-[480px] sm:rounded-xl"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <h2 className="text-[14px] font-semibold text-text">Share account {accountLogin}</h2>
            <p className="mt-0.5 text-[11px] text-text-dim">
              Give another Veritas user read-only access. They see this account&apos;s journal,
              balances and reconciliation — never your notes, thesis, or settings, and they
              cannot edit anything.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="ml-3 shrink-0 rounded-md border border-line p-2 text-text-mid hover:bg-bg-2 hover:text-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4">
          <form onSubmit={grant} className="flex items-center gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="viewer@email.com"
              disabled={busy}
              className="flex-1 rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors focus:border-green disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-2 px-3 py-2 text-[10.5px] font-bold uppercase tracking-eyebrow text-text-mid transition-colors hover:border-green hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UserPlus size={14} /> Share
            </button>
          </form>

          {error && (
            <div className="mt-3 rounded-md border border-red/40 bg-red/10 px-3 py-2 text-[11px] text-red">
              {error}
            </div>
          )}
          {notice && !error && (
            <div className="mt-3 rounded-md border border-green/40 bg-green/10 px-3 py-2 text-[11px] text-green">
              {notice}
            </div>
          )}

          <div className="mt-4">
            <div className="mb-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
              People with access ({active.length})
            </div>
            {active.length === 0 ? (
              <div className="rounded-md border border-line bg-bg-2 px-3 py-3 text-[11px] text-text-dim">
                Not shared with anyone yet.
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {active.map((v) => (
                  <li
                    key={v.viewer_email}
                    className="flex items-center justify-between rounded-md border border-line bg-bg-2 px-3 py-2"
                  >
                    <span className="text-[12px] text-text">{v.viewer_email}</span>
                    <button
                      onClick={() => revoke(v.viewer_email)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[10px] uppercase tracking-eyebrow text-text-mid hover:border-red hover:text-red disabled:opacity-50"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
